import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { ConfigService } from '@venecia/services';
import { UrlBuilder } from '@venecia/builders';
import { TemplateHelper } from '@venecia/apis/base';

import { Bearer } from './model/bearer.model';

@Injectable({ providedIn: 'root' })
export class OAuthApi {
    private readonly http = inject(HttpClient);
    private readonly configService = inject(ConfigService);

    private urlBuilder?: UrlBuilder;

    protected constructor() {
        this.configService
            .select('http.rest.auth.url')
            .pipe(take(1))
            .subscribe((value: string) => {
                this.urlBuilder = new UrlBuilder(value, '/api/oauth');
            });
    }

    buildAuthorizeUrl(query: Record<string, string>): string {
        const url = this.urlBuilder!.build('/authorize');

        const params = new URLSearchParams();
        Object.entries(query)
            .forEach(([k, v]) => params.set(k, v));

        return `${url}?${params.toString()}`;
    }

    tokenAuthorizationCode(command: {
        clientId: string;
        code: string;
        redirectUri: string;
        codeVerifier: string;
    }): Observable<Bearer> {
        const url = this.urlBuilder!.build('/token');

        const body = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('client_id', command.clientId)
            .set('code', command.code)
            .set('redirect_uri', command.redirectUri)
            .set('code_verifier', command.codeVerifier);

        return this.http.post<Bearer>(url, body.toString(), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            withCredentials: true,
        }).pipe(
            TemplateHelper.applyRetry(),
            catchError((err) => TemplateHelper.handleError(err))
        );
    }

    tokenRefresh(command: {
        clientId: string;
        refreshToken: string;
    }): Observable<Bearer> {
        const url = this.urlBuilder!.build('/token');

        const body = new HttpParams()
            .set('grant_type', 'refresh_token')
            .set('client_id', command.clientId)
            .set('refresh_token', command.refreshToken);

        return this.http.post<Bearer>(url, body.toString(), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            withCredentials: true,
        }).pipe(
            TemplateHelper.applyRetry(),
            catchError((err) => TemplateHelper.handleError(err))
        );
    }
}
